import { AxiosError } from "axios";
import {
    UseMutationOptions,
    UseMutationResult,
    useMutation,
    useQueryClient,
} from "react-query";

import { ICustomerGroup } from "../../types";
import { api } from "../../utils";
import { useAuth } from "../useAuth";

export interface IUpdateCustomerGroupMutationParameter {
    name: string;
    description: string;
    customerIds: string[];
    customerGroupId: string;
    importCustomersFile: File | null;
}

export const useUpdateCustomerGroup = (
    invalidQueryKeys?: string | string[],
    options?: UseMutationOptions<
        ICustomerGroup,
        AxiosError,
        IUpdateCustomerGroupMutationParameter,
        unknown
    >,
): UseMutationResult<
    ICustomerGroup,
    AxiosError,
    IUpdateCustomerGroupMutationParameter
> => {
    const auth = useAuth();
    const queryClient = useQueryClient();

    return useMutation<
        ICustomerGroup,
        AxiosError,
        IUpdateCustomerGroupMutationParameter
    >({
        mutationFn: async (
            parameters: IUpdateCustomerGroupMutationParameter,
        ): Promise<ICustomerGroup> => {
            if (!auth.token) {
                throw new Error(
                    "Cannot update customer group without authorization token.",
                );
            }

            return await api.CustomerGroup.update({
                ...parameters,
                token: auth.token,
            });
        },
        ...options,
        onSuccess: (
            customerGroup: ICustomerGroup,
            variables: IUpdateCustomerGroupMutationParameter,
            context: unknown,
        ) => {
            if (invalidQueryKeys !== undefined) {
                queryClient.invalidateQueries(invalidQueryKeys);
            }

            options?.onSuccess?.(customerGroup, variables, context);
        },
    });
};
